import type { ChatModelSelection } from "@/schema/chat-thread"

type ModelStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">

const MODEL_STORAGE_KEY = "chat-model-selection"
const WEEKLY_SELECTION_KEY_PREFIX = "chat-model-selection-week:"
const DEFAULT_MODEL: ChatModelSelection = "gpt_latest"

const WEEKLY_DEFAULTS: Record<ChatModelSelection, ChatModelSelection> = {
  gpt_latest: "gpt_latest",
  gpt_advanced: "gpt_latest",
  claude_sonnet_latest: "claude_sonnet_latest",
  claude_opus_latest: "claude_sonnet_latest",
}

const MODEL_ALIASES: Record<string, ChatModelSelection> = {
  gpt_latest: "gpt_latest",
  gpt_advanced: "gpt_advanced",
  gpt_low: "gpt_latest",
  gpt_5_2: "gpt_latest",
  gpt_5_4: "gpt_latest",
  gpt_5_5: "gpt_latest",
  claude_sonnet_latest: "claude_sonnet_latest",
  claude_sonnet_4_5: "claude_sonnet_latest",
  claude_sonnet_4_6: "claude_sonnet_latest",
  claude_opus_latest: "claude_opus_latest",
  claude_opus_4_6: "claude_opus_latest",
  claude_opus_4_7: "claude_opus_latest",
  gemini_3_flash: "gpt_latest",
  gemini_3_pro: "gpt_latest",
  gemini_flash_latest: "gpt_latest",
  gemini_pro_latest: "gpt_latest",
}

const normalizeModel = (value?: string | null): ChatModelSelection | null =>
  value && Object.hasOwn(MODEL_ALIASES, value) ? MODEL_ALIASES[value] : null

const easternClock = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York",
  year: "numeric",
  month: "numeric",
  day: "numeric",
  hour: "numeric",
  hourCycle: "h23",
})

// UTC fields represent Eastern wall-clock time, independent of the browser's zone.
const easternDate = (now: Date): Date => {
  const parts = easternClock.formatToParts(now)
  const part = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((value) => value.type === type)?.value)
  return new Date(Date.UTC(part("year"), part("month") - 1, part("day"), part("hour")))
}

const currentWeeklyReset = (now: Date): Date => {
  const eastern = easternDate(now)
  const reset = new Date(eastern)
  reset.setUTCDate(reset.getUTCDate() - ((reset.getUTCDay() + 6) % 7))
  reset.setUTCHours(6, 0, 0, 0)
  if (eastern < reset) reset.setUTCDate(reset.getUTCDate() - 7)
  return reset
}

export const getNextChatModelReset = (now = new Date()): Date => {
  const reset = currentWeeklyReset(now)
  reset.setUTCDate(reset.getUTCDate() + 7)
  // Monday at 6 AM is outside DST transitions; use that Monday's Eastern offset.
  const offset = easternDate(reset).getTime() - reset.getTime()
  return new Date(reset.getTime() - offset)
}

const selectionWeek = (now: Date): string => currentWeeklyReset(now).toISOString().slice(0, 10)

// Only an explicit selection grants premium use until Monday at 6 AM Eastern.
// Reading an old thread or restoring storage must never renew that selection.
export const getWeeklyChatModel = (
  model: ChatModelSelection,
  storage: ModelStorage,
  now = new Date()
): ChatModelSelection => {
  const weeklyDefault = WEEKLY_DEFAULTS[model]
  return storage.getItem(`${WEEKLY_SELECTION_KEY_PREFIX}${weeklyDefault}`) === selectionWeek(now)
    ? model
    : weeklyDefault
}

export const getInitialChatModel = (
  storage: ModelStorage,
  threadModel?: string | null,
  now = new Date()
): ChatModelSelection => {
  const storedModel = normalizeModel(storage.getItem(MODEL_STORAGE_KEY))
  const weeklyStoredModel = storedModel ? getWeeklyChatModel(storedModel, storage, now) : null
  if (weeklyStoredModel) storage.setItem(MODEL_STORAGE_KEY, weeklyStoredModel)

  const model = normalizeModel(threadModel) ?? weeklyStoredModel ?? DEFAULT_MODEL
  return getWeeklyChatModel(model, storage, now)
}

export const saveChatModel = (model: ChatModelSelection, storage: ModelStorage, now = new Date()): void => {
  storage.setItem(MODEL_STORAGE_KEY, model)
  const weeklyDefault = WEEKLY_DEFAULTS[model]
  const weekKey = `${WEEKLY_SELECTION_KEY_PREFIX}${weeklyDefault}`
  if (model === weeklyDefault) {
    storage.removeItem(weekKey)
  } else {
    storage.setItem(weekKey, selectionWeek(now))
  }
}
